/**
 * SMS Service — wraps the smsethiopia.com v2 API.
 *
 * smsethiopia.com has NO CORS support — browser calls are always blocked.
 * All requests must go through a server-side proxy:
 *
 *   Dev:        Vite proxies /api/sms/* → smsethiopia.com/api/v2/sms/* (Node.js)
 *   Production: /sms-proxy.php on mirtsew.com (PHP curl, server-side)
 *
 * The API key lives server-side only (in vite.config.js for dev, in sms-proxy.php for prod).
 */

class SmsService {
  /**
   * Normalise phone number to Ethiopian international format: 251XXXXXXXXX
   *   +251911639555  → 251911639555
   *   0911639555     → 251911639555  (local 0-prefixed format)
   *   251911639555   → 251911639555  (already correct)
   */
  normaliseNumber(raw) {
    let n = raw.trim().replace(/[\s\-().]/g, "");
    if (n.startsWith("+")) n = n.slice(1);
    if (/^0\d{9}$/.test(n)) n = "251" + n.slice(1);
    return n;
  }

  /**
   * Send an SMS.
   * Never throws — always returns an object.
   * Success: { sent: true, id, status, segments, description }
   * Failure: { sent: false, error, rawStatus?, rawBody? }
   */
  async send(msisdn, text) {
    const normalised = this.normaliseNumber(msisdn);

    // Dev → Vite proxy (injects API key via proxyReq hook in vite.config.js)
    // Prod → PHP proxy on same domain (no CORS, API key is hardcoded in PHP file)
    const url = import.meta.env.DEV
      ? "/api/sms/send"
      : "/sms-proxy.php?action=send";

    console.log("[SMS] Sending to:", normalised, "→", url);

    let res, rawBody;
    try {
      res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ msisdn: normalised, text }),
      });
      rawBody = await res.text();
      console.log("[SMS] Response:", res.status, rawBody);
    } catch (networkErr) {
      console.error("[SMS] Network error:", networkErr.message);
      return {
        sent: false,
        id: null,
        status: "NETWORK_ERROR",
        error: `Network error: ${networkErr.message}`,
      };
    }

    if (!res.ok) {
      let apiMessage = rawBody;
      try {
        const parsed = JSON.parse(rawBody);
        apiMessage =
          parsed.message || parsed.error || parsed.description || rawBody;
      } catch (_) {}
      return {
        sent: false,
        id: null,
        status: "API_ERROR",
        rawStatus: res.status,
        error: `HTTP ${res.status}: ${apiMessage}`,
        rawBody,
      };
    }

    try {
      const data = JSON.parse(rawBody);
      return { sent: true, ...data };
    } catch (_) {
      return {
        sent: false,
        id: null,
        status: "PARSE_ERROR",
        error: "Could not parse API response.",
        rawBody,
      };
    }
  }

  /**
   * Get live delivery status.
   */
  async getStatus(messageId) {
    if (!messageId) return { id: messageId, status: "UNKNOWN" };

    const url = import.meta.env.DEV
      ? `/api/sms/${messageId}`
      : `/sms-proxy.php?action=status&id=${encodeURIComponent(messageId)}`;

    try {
      const res = await fetch(url);
      if (!res.ok) return { id: messageId, status: "UNKNOWN" };
      return await res.json();
    } catch (_) {
      return { id: messageId, status: "UNKNOWN" };
    }
  }

  buildApprovalMessage(fullName, tempPassword, loginUrl, userInfo = {}) {
    const loginLine =
      userInfo.isAdmin || userInfo.isModerator
        ? `Login: ${loginUrl}`
        : `Login: MirtSew mobile app`;
    return (
      `MirtSew: Dear ${fullName}, registration approved!\n` +
      `Temporary Password: ${tempPassword}\n` +
      loginLine
    );
  }

  buildPasswordResetMessage(resetLink) {
    return `MirtSew: Reset your password (expires 10min):\n${resetLink}`;
  }
}

export default new SmsService();
